import React, { useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Image,
  Divider,
  Button,
  Progress
} from '@lockerpm/design';

import {
  PlusOutlined,
  ImportOutlined,
  SecurityScanOutlined,
  SettingOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  PlusCircleFilled
} from "@ant-design/icons";

import {
  green
} from '@ant-design/colors';

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import images from "../../../assets/images";
import global from "../../../config/global";

import storeActions from "../../../store/actions";

const WelcomeTour = () => {
  const { t } = useTranslation();
  const {
    PageHeader
  } = commonComponents;
  const {
    DocLink
  } = itemsComponents;

  const tutorialProcess = useSelector((state) => state.onboarding.tutorialProcess) || [];
  const locale = useSelector((state) => state.system.locale);
  const isMobile = useSelector((state) => state.system.isMobile);

  const handleStart = (stepIndex) => {
    if (stepIndex === 5) {
      global.store.dispatch(storeActions.updateGetExtensionVisible(true))
    } else {
      global.store.dispatch(storeActions.updateStepIndex(stepIndex));
      if ([0, 2, 3].includes(stepIndex)) {
        if ([2, 3].includes(stepIndex) && isMobile) {
          global.store.dispatch(storeActions.updateRespCollapsed(false));
        }
        global.navigate(global.keys.VAULT);
      } else if (stepIndex === 1) {
        global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
      }
      setTimeout(() => {
        global.store.dispatch(storeActions.updateTutorialRunning(true));
      }, 500);
    }
  }

  const tutorialSteps = useMemo(() => {
    return [
      {
        i18nKey: 'add_pw',
        img: images.WelAddPWImg,
        button: {
          icon: <PlusOutlined />,
          action: () => handleStart(0)
        }
      },
      {
        i18nKey: 'import_file',
        img: images.WelImportFileImg,
        docKey: 'IMPORT_AND_EXPORT',
        button: {
          icon: <ImportOutlined />,
          action: () => handleStart(1)
        }
      },
      {
        i18nKey: 'browse_items',
        img: images.WelBrowseItemImg,
        button: {
          icon: <SecurityScanOutlined />,
          action: () => handleStart(2)
        }
      },
      {
        i18nKey: 'view_settings',
        img: images.WelViewSettingsImg,
        button: {
          icon: <SettingOutlined />,
          action: () => handleStart(3)
        }
      },
      {
        i18nKey: 'view_profile',
        img: images.WelViewProfileImg,
        button: {
          icon: <CheckOutlined />,
          action: () => handleStart(4)
        }
      },
      {
        i18nKey: 'view_autofill',
        img: images.WelViewAutofillImg,
        button: {
          icon: <ArrowRightOutlined />,
          action: () => handleStart(5)
        }
      },
    ]
  }, [locale])

  return (
    <div className={`tutorial-steps layout-content`}>
      <PageHeader
        title={t('welcome_tour.title')}
        actions={[]}
      />
      <div className="mt-4">
        <div className="mb-6">
          <Button
            type="primary"
            ghost
            size="large"
            icon={<ArrowRightOutlined />}
            onClick={() => {
              const indexStart = tutorialSteps.findIndex((step, index) => !tutorialProcess.includes(step.i18nKey) || index === 0);
              handleStart(indexStart);
            }}
          >
            {t(`welcome_tour.btn`)}
          </Button>
          <div className="flex items-center mt-2">
            <Progress
              style={{ width: 300, margin: 0 }}
              percent={tutorialProcess.length / tutorialSteps.length * 100}
              showInfo={false}
              strokeColor={green[6]}
            />
            <p className="ml-4">{tutorialProcess.length}/{tutorialSteps.length} {t('welcome_tour.steps_completed')}</p>
          </div>
        </div>
        {
          tutorialSteps.map((item, index) => <div
            key={index}
            className={tutorialProcess.includes(item.i18nKey) ? 'opacity-50' : ''}
          >
            {
              index > 0 && <Divider />
            }
            <div className="flex flex-wrap items-center">
              <div className="relative">
                <Image
                  src={item.img}
                  preview={false}
                />
                <div className="absolute text-primary top-0 right-0 text-xl">
                  {
                    tutorialProcess.includes(item.i18nKey) ? <CheckCircleFilled /> : <PlusCircleFilled />
                  }
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="font-semibold">{t(`welcome_tour.steps.${item.i18nKey}.title`)}</p>
                <p className="text-black-500 mt-2">{t(`welcome_tour.steps.${item.i18nKey}.desc`)}</p>
                <div className="flex items-center mt-4">                  
                  <Button
                    type="primary"
                    icon={item.button.icon}
                    onClick={() => item.button.action()}
                  >
                    {t(`welcome_tour.steps.${item.i18nKey}.btn`)}
                  </Button>
                  {
                    item.docKey && <DocLink
                      className="ml-4 font-semibold"
                      title={t(`common.learn_more`)}
                      docKey={item.docKey}
                    />
                  }
                </div>
              </div>
            </div>
          </div>)
        }
      </div>
    </div>
  );
}

export default WelcomeTour;